/**
 * ChatContext - Global state management for booking-based chat system
 * Manages chat bubble visibility, active chats list, and unread counts
 */

import { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react'
import { supabase } from '../lib/supabase'
import { useAuth } from './AuthContext'

const ChatContext = createContext({})

// Browser storage only remembers the drawer between page loads; the chat must work without it.
// On iPhones it can be unavailable or throw: private browsing, site data disabled, and the in-app
// browsers that open links from Gmail, Instagram or Messenger. A throw here used to abort the tap
// on a chat before the conversation opened (Lida Taheri, 9 Sep 2026), so every access is guarded.
const storage = {
  get(key) {
    try { return window.localStorage.getItem(key) } catch { return null }
  },
  set(key, value) {
    try { window.localStorage.setItem(key, value) } catch { /* remembering is optional */ }
  },
  remove(key) {
    try { window.localStorage.removeItem(key) } catch { /* remembering is optional */ }
  }
}

export const useChat = () => {
  const context = useContext(ChatContext)
  if (!context) {
    throw new Error('useChat must be used within ChatProvider')
  }
  return context
}

/** The chat context, or null outside ChatProvider (for an optional shortcut such as the booking card's chat button). */
export const useChatOptional = () => useContext(ChatContext)

export const ChatProvider = ({ children }) => {
  const { user, profile } = useAuth()
  const [isBubbleOpen, setIsBubbleOpen] = useState(false)
  const [currentBookingId, setCurrentBookingId] = useState(null)
  const [currentThreadId, setCurrentThreadId] = useState(null)
  const [shouldOpenAdminChat, setShouldOpenAdminChat] = useState(false)
  const [activeChats, setActiveChats] = useState([])
  const [totalUnreadCount, setTotalUnreadCount] = useState(0)
  const [isLoading, setIsLoading] = useState(false)
  const isMountedRef = useRef(true)

  /**
   * Fetch all active chats for the current user
   */
  const fetchActiveChats = useCallback(async () => {
    if (!user) {
      setActiveChats([])
      setTotalUnreadCount(0)
      return
    }

    // Don't show chat bubble for admins (they use admin panel)
    if (profile?.role === 'admin') {
      setActiveChats([])
      setTotalUnreadCount(0)
      return
    }

    try {
      setIsLoading(true)

      const { data: { session } } = await supabase.auth.getSession()
      if (!session) {
        setActiveChats([])
        setTotalUnreadCount(0)
        return
      }

      const response = await fetch('/api/messages/my-chats', {
        headers: {
          'Authorization': `Bearer ${session.access_token}`
        }
      })

      if (!response.ok) {
        throw new Error('Failed to fetch active chats')
      }

      const result = await response.json()

      if (!isMountedRef.current) return

      setActiveChats(result.chats || [])

      // Calculate total unread count
      const total = (result.chats || []).reduce((sum, chat) => sum + (chat.unreadCount || 0), 0)
      setTotalUnreadCount(total)
    } catch (error) {
      console.error('Error fetching active chats:', error)
      if (isMountedRef.current) {
        setActiveChats([])
        setTotalUnreadCount(0)
      }
    } finally {
      if (isMountedRef.current) {
        setIsLoading(false)
      }
    }
  }, [user, profile])

  /**
   * Toggle chat bubble open/closed
   */
  const toggleBubble = useCallback(() => {
    setIsBubbleOpen(prev => !prev)

    // Store preference in localStorage
    const newState = !isBubbleOpen
    storage.set('chatBubbleOpen', JSON.stringify(newState))
  }, [isBubbleOpen])

  /**
   * Open chat for a specific booking
   */
  const openChat = useCallback((bookingId, threadId = null) => {
    setCurrentBookingId(bookingId)
    setCurrentThreadId(threadId)
    setShouldOpenAdminChat(false)
    setIsBubbleOpen(true)
    storage.set('chatBubbleOpen', 'true')
    storage.set('currentChatBookingId', bookingId)
    if (threadId) {
      storage.set('currentChatThreadId', threadId)
    } else {
      storage.remove('currentChatThreadId')
    }
  }, [])

  /**
   * Open admin support chat
   */
  const openAdminChat = useCallback(() => {
    setCurrentBookingId(null)
    setCurrentThreadId(null)
    setShouldOpenAdminChat(true)
    setIsBubbleOpen(true)
    storage.set('chatBubbleOpen', 'true')
    storage.set('shouldOpenAdminChat', 'true')
    storage.remove('currentChatBookingId')
    storage.remove('currentChatThreadId')
  }, [])

  /**
   * Close current chat and return to chat list
   */
  const closeChat = useCallback(() => {
    setCurrentBookingId(null)
    setCurrentThreadId(null)
    setShouldOpenAdminChat(false)
    storage.remove('currentChatBookingId')
    storage.remove('currentChatThreadId')
    storage.remove('shouldOpenAdminChat')
  }, [])

  /**
   * Minimize/close the bubble
   */
  const minimizeBubble = useCallback(() => {
    setIsBubbleOpen(false)
    setCurrentBookingId(null)
    setCurrentThreadId(null)
    setShouldOpenAdminChat(false)
    storage.set('chatBubbleOpen', 'false')
    storage.remove('currentChatBookingId')
    storage.remove('currentChatThreadId')
    storage.remove('shouldOpenAdminChat')
  }, [])

  /**
   * Decrement unread count for a specific booking (when message is read)
   */
  const decrementUnreadCount = useCallback((bookingId, amount = 1) => {
    setActiveChats(prev =>
      prev.map(chat =>
        chat.bookingId === bookingId
          ? { ...chat, unreadCount: Math.max(0, (chat.unreadCount || 0) - amount) }
          : chat
      )
    )
    setTotalUnreadCount(prev => Math.max(0, prev - amount))
  }, [])

  /**
   * Increment unread count for a specific booking (when new message arrives)
   */
  const incrementUnreadCount = useCallback((bookingId, amount = 1) => {
    setActiveChats(prev =>
      prev.map(chat =>
        chat.bookingId === bookingId
          ? { ...chat, unreadCount: (chat.unreadCount || 0) + amount }
          : chat
      )
    )
    setTotalUnreadCount(prev => prev + amount)
  }, [])

  /**
   * Update last message timestamp for a booking
   */
  const updateLastMessage = useCallback((bookingId, timestamp, messagePreview, threadId = null) => {
    setActiveChats(prev => {
      const updated = prev.map(chat =>
        chat.bookingId === bookingId && (!threadId || chat.threadId === threadId)
          ? { ...chat, lastMessageAt: timestamp, lastMessagePreview: messagePreview }
          : chat
      )
      // Sort by most recent message
      return updated.sort((a, b) => {
        const aTime = a.lastMessageAt ? new Date(a.lastMessageAt) : new Date(0)
        const bTime = b.lastMessageAt ? new Date(b.lastMessageAt) : new Date(0)
        return bTime - aTime
      })
    })
  }, [])

  // Fetch active chats on mount and when user changes
  useEffect(() => {
    isMountedRef.current = true

    if (user) {
      fetchActiveChats()

      // Restore bubble state from localStorage
      const savedBubbleState = storage.get('chatBubbleOpen')
      const savedBookingId = storage.get('currentChatBookingId')
      const savedThreadId = storage.get('currentChatThreadId')
      const savedAdminChat = storage.get('shouldOpenAdminChat')

      if (savedBubbleState === 'true') {
        setIsBubbleOpen(true)
      }

      if (savedBookingId) {
        setCurrentBookingId(savedBookingId)
      }

      if (savedThreadId) {
        setCurrentThreadId(savedThreadId)
      }

      if (savedAdminChat === 'true') {
        setShouldOpenAdminChat(true)
      }
    }

    return () => {
      isMountedRef.current = false
    }
  }, [user, fetchActiveChats])

  // Set up real-time subscription for booking updates (new bookings, unread counts)
  useEffect(() => {
    if (!user || !profile || profile.role === 'admin') return

    // Subscribe to bookings table changes for unread count updates
    const channel = supabase
      .channel('chat_updates')
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'bookings',
          filter: profile.role === 'photographer'
            ? `photographer_id=eq.${profile.id}`
            : `customer_id=eq.${user.id}`
        },
        (payload) => {
          // Refetch chats when unread counts change
          if (
            payload.new.unread_count_customer !== payload.old.unread_count_customer ||
            payload.new.unread_count_photographer !== payload.old.unread_count_photographer
          ) {
            fetchActiveChats()
          }
        }
      )
      .subscribe()

    return () => {
      supabase.removeChannel(channel)
    }
  }, [user, profile, fetchActiveChats])

  // Polling fallback (refresh every 30 seconds) for unread counts
  useEffect(() => {
    if (!user || !profile || profile.role === 'admin') return

    const interval = setInterval(() => {
      fetchActiveChats()
    }, 30000) // 30 seconds

    return () => clearInterval(interval)
  }, [user, profile, fetchActiveChats])

  const value = {
    // State
    isBubbleOpen,
    currentBookingId,
    currentThreadId,
    shouldOpenAdminChat,
    activeChats,
    totalUnreadCount,
    isLoading,

    // Actions
    toggleBubble,
    openChat,
    openAdminChat,
    closeChat,
    minimizeBubble,
    fetchActiveChats,
    decrementUnreadCount,
    incrementUnreadCount,
    updateLastMessage
  }

  return (
    <ChatContext.Provider value={value}>
      {children}
    </ChatContext.Provider>
  )
}
